import usePermissions from '@/hooks/usePermissions';
import { useEditAccountDirectPayMutation } from '@/services/accounts';
import { isErrorWithData } from '@/services/helpers';
import { useGetDirectPayMaterialVendorsQuery } from '@/services/organizations';
import {
  Box,
  Button,
  Modal,
  SelectInputNative,
  TextareaInput,
  TextInput,
  toast,
} from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  DirectPayMaterialVendorAliasOptions,
  DirectPayMaterialVendorDisplayNameOptions,
  OrganizationDirectPaySettingsV2,
  UserPermissions,
} from 'types';

export const DirectPayOptInOutModal = ({
  isModalOpen,
  handleCloseModal,
  type,
  accountId,
  directPayMaterialVendorAliasOnAccount,
  orgDirectPaySettings,
  isNtpComplete = true,
  isInstallPending = true,
  hasShipmentConfirmation = false,
  hasFinalizationInvoice = false,
  hasActiveQuote = true,
}: {
  isModalOpen: boolean;
  handleCloseModal: () => void;
  type: 'optIn' | 'optOut';
  accountId: string;
  directPayMaterialVendorAliasOnAccount: DirectPayMaterialVendorAliasOptions;
  orgDirectPaySettings?: OrganizationDirectPaySettingsV2;
  isNtpComplete?: boolean;
  isInstallPending?: boolean;
  hasShipmentConfirmation?: boolean;
  hasFinalizationInvoice?: boolean;
  hasActiveQuote?: boolean;
}) => {
  const [editDirectPay, { isLoading }] = useEditAccountDirectPayMutation();
  const canOverride = usePermissions().includes(UserPermissions.lightReachDirectPayOverride);
  const [view, setView] = useState<'default' | 'override'>('default');
  const [overrideReason, setOverrideReason] = useState('');
  const [salesforceTicketNumber, setSalesforceTicketNumber] = useState('');
  const [selectedVendor, setSelectedVendor] = useState<DirectPayMaterialVendorAliasOptions | ''>('');
  const [filteredVendors, setFilteredVendors] = useState<
    Array<{ value: DirectPayMaterialVendorAliasOptions; label: string }>
  >([]);
  const [selectedSubCompanyId, setSelectedSubCompanyId] = useState('');

  const { data: directPayMaterialVendors } = useGetDirectPayMaterialVendorsQuery();

  const availableVendorsForOrg = useMemo(() => {
    const availableVendors: { value: DirectPayMaterialVendorAliasOptions; label: string }[] = [];

    // Add the main vendor if it has an installer identifier
    if (orgDirectPaySettings?.directPayMaterialVendorAlias && orgDirectPaySettings?.directPayInstallerIdentifier) {
      availableVendors.push({
        value: orgDirectPaySettings.directPayMaterialVendorAlias,
        label: DirectPayMaterialVendorDisplayNameOptions[orgDirectPaySettings.directPayMaterialVendorAlias],
      });
    }

    // Add vendors from state overrides if they have installer identifiers
    if (orgDirectPaySettings?.stateOverrides?.length) {
      orgDirectPaySettings.stateOverrides.forEach((override) => {
        if (override.directPayMaterialVendorAlias && override.directPayInstallerIdentifier) {
          availableVendors.push({
            value: override.directPayMaterialVendorAlias,
            label: DirectPayMaterialVendorDisplayNameOptions[override.directPayMaterialVendorAlias],
          });
        }
      });
    }

    // Add vendors from additional opt-in vendors if they have installer identifiers
    if (orgDirectPaySettings?.additionalOptInVendors?.length) {
      orgDirectPaySettings.additionalOptInVendors.forEach((vendor) => {
        if (vendor.directPayMaterialVendorAlias && vendor.directPayInstallerIdentifier) {
          availableVendors.push({
            value: vendor.directPayMaterialVendorAlias,
            label: DirectPayMaterialVendorDisplayNameOptions[vendor.directPayMaterialVendorAlias],
          });
        }
      });
    }

    // Dedupe by alias — the same vendor can appear in the default settings, a state override, and
    // an additional opt-in vendor entry, but the dropdown should only list it once.
    return Array.from(new Map(availableVendors.map((vendor) => [vendor.value, vendor])).values());
  }, [orgDirectPaySettings]);

  // Distinct configured sub-company ids per vendor alias, across the org's default settings, state
  // overrides, and additional opt-in vendors — a vendor can be configured with different
  // sub-companies in each of those places.
  const subCompanyIdsByVendor = useMemo(() => {
    const map = new Map<string, Set<string>>();
    const add = (alias?: string, subId?: string) => {
      if (!alias) return;
      if (!map.has(alias)) map.set(alias, new Set());
      if (subId) map.get(alias)!.add(subId);
    };
    add(orgDirectPaySettings?.directPayMaterialVendorAlias, orgDirectPaySettings?.subCompanyId);
    orgDirectPaySettings?.stateOverrides?.forEach((o) => add(o.directPayMaterialVendorAlias, o.subCompanyId));
    orgDirectPaySettings?.additionalOptInVendors?.forEach((v) =>
      add(v.directPayMaterialVendorAlias, v.subCompanyId),
    );
    return map;
  }, [orgDirectPaySettings]);

  const subCompanyOptionsForVendor = useCallback(
    (alias: string) => {
      const ids = Array.from(subCompanyIdsByVendor.get(alias) ?? []);
      const catalogVendor = directPayMaterialVendors?.materialVendors?.find(
        (v) => v.directPayMaterialVendorAlias === alias,
      );
      return ids.map((id) => ({
        value: id,
        label: catalogVendor?.subCompanies?.find((sc) => sc.id === id)?.name ?? id,
      }));
    },
    [subCompanyIdsByVendor, directPayMaterialVendors],
  );

  const subCompanyOptions = useMemo(
    () => (selectedVendor ? subCompanyOptionsForVendor(selectedVendor) : []),
    [selectedVendor, subCompanyOptionsForVendor],
  );

  useEffect(() => {
    setSelectedSubCompanyId('');
  }, [selectedVendor]);

  useEffect(() => {
    if (!isModalOpen) {
      setView('default');
      setOverrideReason('');
      setSalesforceTicketNumber('');
    }
  }, [isModalOpen]);

  const updateDirectPay = useCallback(async () => {
    try {
      // When opting in, use the selected vendor if available, otherwise use the vendor that's already on the account(i.e. for opting out)
      const provider = type === 'optIn' && selectedVendor ? selectedVendor : directPayMaterialVendorAliasOnAccount;

      await editDirectPay({
        enabled: type === 'optIn',
        provider,
        id: accountId,
        ...(type === 'optIn' && selectedSubCompanyId ? { subCompanyId: selectedSubCompanyId } : {}),
      }).unwrap();
      toast.success('Direct Pay update submitted');
      handleCloseModal();
    } catch (e) {
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(`Error updating direct pay: ${errorMessage}`);
      } else {
        console.error(e);
        toast.error('Error updating direct pay');
      }
    }
  }, [
    type,
    selectedVendor,
    selectedSubCompanyId,
    directPayMaterialVendorAliasOnAccount,
    accountId,
    editDirectPay,
    handleCloseModal,
  ]);

  const handleDismiss = () => {
    if (!isLoading) {
      handleCloseModal();
    }
  };

  const handleVendorChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedVendor(event.target.value as DirectPayMaterialVendorAliasOptions);
  }, []);

  const handleSubCompanyChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSubCompanyId(event.target.value);
  }, []);

  const handleStartOverride = useCallback(() => setView('override'), []);
  const handleBackFromOverride = useCallback(() => setView('default'), []);
  const handleReasonChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOverrideReason(event.target.value);
  }, []);
  const handleTicketChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSalesforceTicketNumber(event.target.value);
  }, []);

  const submitOverride = useCallback(async () => {
    try {
      // Match updateDirectPay: vendor/sub-company selection only applies to opt-in. On opt-out these
      // stay unset, so fall back to the account's own vendor and never send a sub-company — the
      // async processing write stamps directPay.{vendor,subCompany}, so guarding here avoids ever
      // mutating them on an opt-out override.
      const provider = type === 'optIn' && selectedVendor ? selectedVendor : directPayMaterialVendorAliasOnAccount;
      await editDirectPay({
        enabled: type === 'optIn',
        provider,
        id: accountId,
        ...(type === 'optIn' && selectedSubCompanyId ? { subCompanyId: selectedSubCompanyId } : {}),
        override: { reason: overrideReason, ...(salesforceTicketNumber ? { salesforceTicketNumber } : {}) },
      }).unwrap();
      toast.success('Direct Pay override submitted');
      handleCloseModal();
    } catch (e) {
      if (isErrorWithData(e)) {
        toast.error(`Error updating direct pay: ${e.data.message}`);
      } else {
        console.error(e);
        toast.error('Error updating direct pay');
      }
    }
  }, [
    type,
    selectedVendor,
    selectedSubCompanyId,
    directPayMaterialVendorAliasOnAccount,
    accountId,
    editDirectPay,
    handleCloseModal,
    overrideReason,
    salesforceTicketNumber,
  ]);

  const isOverrideConfirmDisabled =
    isLoading ||
    !overrideReason.trim() ||
    (type === 'optIn' && !selectedVendor) ||
    (type === 'optIn' && !hasActiveQuote);

  useEffect(() => {
    if (type === 'optIn' && availableVendorsForOrg.length > 0) {
      const currentVendor = availableVendorsForOrg.find((v) => v.value === directPayMaterialVendorAliasOnAccount);
      setSelectedVendor(currentVendor ? currentVendor.value : availableVendorsForOrg[0].value);
      setFilteredVendors(availableVendorsForOrg);
    } else {
      setFilteredVendors(availableVendorsForOrg);
    }
  }, [type, availableVendorsForOrg, directPayMaterialVendorAliasOnAccount]);

  const isBlockedByNtp = type === 'optIn' && !isNtpComplete;
  const isBlockedByInstall = type === 'optIn' && isNtpComplete && !isInstallPending;
  const isBlockedByShipment = type === 'optOut' && hasShipmentConfirmation;
  const isBlockedByInvoice = type === 'optOut' && hasFinalizationInvoice;

  const buttonStyle = useMemo(() => ({ whiteSpace: 'nowrap' as const }), []);

  // Only surface the override when a gate is actually blocking the normal Confirm. Offering it when
  // nothing is blocked would let a routine opt-in/out be submitted as an override — recording an
  // empty-bypassedGates audit entry and masking the normal history event.
  const hasBlockingGate = isBlockedByNtp || isBlockedByInstall || isBlockedByShipment || isBlockedByInvoice;

  const isConfirmDisabled =
    isLoading ||
    (type === 'optIn' &&
      (isBlockedByNtp ||
        isBlockedByInstall ||
        !selectedVendor ||
        (subCompanyOptions.length > 0 && !selectedSubCompanyId))) ||
    isBlockedByShipment ||
    isBlockedByInvoice;

  return (
    <Modal ariaLabelledBy="titleBasic" isOpen={isModalOpen} onDismiss={handleDismiss} maxWidth="5xl">
      <Modal.Header
        id="titleBasic"
        title={`Opt ${type === 'optIn' ? 'In' : 'Out'} of Direct Pay`}
        onDismiss={handleDismiss}
      />
      <Modal.Body childGap="md" background="secondary">
        <Box>
          {view === 'override' ? (
            <Box childGap="md">
              <Box>
                This is a support-only override.{' '}
                {type === 'optIn'
                  ? 'Opting in will bypass the NTP-complete and install-submitted checks for this account.'
                  : 'Opting out will bypass the shipment confirmation and finalization invoice checks for this account.'}{' '}
                A reason is required and is recorded in the account&apos;s history.
              </Box>
              {type === 'optIn' && !hasActiveQuote && (
                <Box
                  background="danger"
                  padding="md"
                  childGap="xs"
                  data-testid="direct-pay-override-no-quote-error"
                  role="alert"
                >
                  <Box fontWeight="bold" color="danger-700">
                    No active quote
                  </Box>
                  <Box display="block">An active quote is required to perform a Direct Pay opt-in override.</Box>
                </Box>
              )}
              <TextareaInput
                id="directPayOverrideReason"
                name="directPayOverrideReason"
                label="Reason for override"
                value={overrideReason}
                onChange={handleReasonChange}
                isRequired
              />
              <TextInput
                id="directPaySalesforceTicket"
                name="directPaySalesforceTicket"
                label="Salesforce ticket number"
                value={salesforceTicketNumber}
                onChange={handleTicketChange}
              />
            </Box>
          ) : type === 'optOut' ? (
            <Box childGap="md">
              <Box>
                {directPayMaterialVendorAliasOnAccount ? (
                  <>
                    Are you sure you want to opt this account out of Direct Pay with{' '}
                    {DirectPayMaterialVendorDisplayNameOptions[directPayMaterialVendorAliasOnAccount]}?
                  </>
                ) : (
                  <>Are you sure you want to opt this account out of Direct Pay?</>
                )}
              </Box>
              {isBlockedByShipment && (
                <Box
                  background="warning"
                  padding="md"
                  childGap="xs"
                  margin="md 0 0 0"
                  data-testid="direct-pay-shipment-warning"
                  role="alert"
                >
                  <Box fontWeight="bold" color="warning-700">
                    Account has shipment confirmation
                  </Box>
                  <Box display="block">
                    Opting out is blocked if shipment confirmation has already been received.
                  </Box>
                </Box>
              )}
              {isBlockedByInvoice && (
                <Box
                  background="warning"
                  padding="md"
                  childGap="xs"
                  margin="md 0 0 0"
                  data-testid="direct-pay-invoice-warning"
                  role="alert"
                >
                  <Box fontWeight="bold" color="warning-700">
                    Account has received finalization invoice
                  </Box>
                  <Box display="block">Opting out is blocked if an invoice has already been received.</Box>
                </Box>
              )}
            </Box>
          ) : (
            <Box childGap="md">
              <Box>
                Please select a direct pay vendor to opt in with from the available vendors for this account&apos;s
                licensed organization:
              </Box>
              {filteredVendors.length > 0 ? (
                <>
                  <SelectInputNative
                    id="directPayVendor"
                    name="directPayVendor"
                    label="Direct Pay Vendor"
                    options={filteredVendors}
                    value={selectedVendor}
                    onChange={handleVendorChange}
                    isRequired
                  />
                  {subCompanyOptions.length > 0 && (
                    <Box margin="sm 0 0 0">
                      <SelectInputNative
                        id="directPaySubCompany"
                        name="directPaySubCompany"
                        label="Sub-Company"
                        options={subCompanyOptions}
                        value={selectedSubCompanyId}
                        onChange={handleSubCompanyChange}
                        isRequired
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Box color="danger">
                  No direct pay vendors are available for this account&apos;s licensed organization. Please setup a
                  direct pay vendor in the licensed organization settings.
                </Box>
              )}
              {isBlockedByNtp && (
                <Box
                  background="warning"
                  padding="md"
                  childGap="xs"
                  margin="md 0 0 0"
                  data-testid="direct-pay-ntp-warning"
                  role="alert"
                >
                  <Box fontWeight="bold" color="warning-700">
                    Account not at NTP
                  </Box>
                  <Box display="block">
                    Opting in is only allowed after the project is NTP complete and before it is Install submitted.
                  </Box>
                  <Box display="block">
                    <strong>NOTE:</strong> Accounts are automatically enrolled with direct pay at NTP based on the
                    organization&apos;s direct pay settings.
                  </Box>
                </Box>
              )}
              {isBlockedByInstall && (
                <Box
                  background="warning"
                  padding="md"
                  childGap="xs"
                  margin="md 0 0 0"
                  data-testid="direct-pay-install-warning"
                  role="alert"
                >
                  <Box fontWeight="bold" color="warning-700">
                    Account already past install submitted
                  </Box>
                  <Box display="block">Direct pay is no longer possible via Palmetto LightReach.</Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Modal.Body>
      <Modal.Footer justifyContent="space-between">
        {view === 'override' ? (
          <>
            <Button onClick={handleBackFromOverride} variant="secondary" tone="neutral" isDisabled={isLoading}>
              Back
            </Button>
            <Button
              variant="primary"
              className="shadow-xs"
              onClick={submitOverride}
              style={buttonStyle}
              isDisabled={isOverrideConfirmDisabled}
              isLoading={isLoading}
            >
              Apply override
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleDismiss} variant="secondary" tone="neutral" isDisabled={isLoading}>
              Cancel
            </Button>
            <Box direction="row" childGap="sm">
              {canOverride && hasBlockingGate && (
                <Button onClick={handleStartOverride} variant="secondary" tone="neutral" isDisabled={isLoading}>
                  Override
                </Button>
              )}
              <Button
                variant="primary"
                className="shadow-xs"
                onClick={updateDirectPay}
                style={buttonStyle}
                isDisabled={isConfirmDisabled}
                isLoading={isLoading}
              >
                Confirm
              </Button>
            </Box>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};
